import { BaseEditorComponent } from "@handsontable/react";
import {
  AtomicBlockUtils,
  ContentBlock,
  ContentState,
  convertToRaw,
  DraftEditorCommand,
  DraftHandleValue,
  EditorState,
  Modifier
} from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import React, {
  FC,
  MouseEventHandler,
  useEffect,
  useRef,
  useState,
} from "react";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg"

import { RefObject } from "react";
import { Editor } from "react-draft-wysiwyg";
import { useDispatch } from "react-redux";
import { showErrorToast } from "store/toast/toastActions";
import TableTimeImageComponent from "../../TableTimeImageComponent";

import {
  AddTimeImageTargets,
  ITableComponentImage,
  ITableTimeImageAttachment,
  ITimeImageData,
} from "store/manageSW/manageSWTypes";
import { ImageScale, SWTypes } from "interfaces/sw/SWInterfaces";
import { addImageNumberToLabel } from "utilities/fileUtilities";
import { downloadTableTimeImage, uploadTableImage } from "store/manageSW/manageSWActions";
import TableImageComponent from "../../TableImageComponent";
import { convertFromHTML } from "draft-convert";
import DeleteImageComponent from "./DeleteImageComponent";
import CustomDropdown from "components/common/CustomDropDown";
import { style } from "@mui/system";

interface EditorComponentProps {
  hotTableRef: RefObject<any>;
  handleImageUpload: (file: string, newHtmlContent: string, rowIndex: number, colIndex: number, scaleOption: ImageScale) => void;
  handleImageScaleUpdate: (newHtmlContent: string, rowIndex: number, colIndex: number, scaleOption: ImageScale) => void;
  disabled: boolean;
  timeImageData?: ITimeImageData;
  enableTimeUpload: boolean;
  swType: SWTypes;
  swGuid: string;
  swVersion: number;
  componentGuid?: string;
  stepGuid?: string;
  timeImageTarget: AddTimeImageTargets;
  updateCellAfterDeleteImg: (newHtmlContent: string, rowIndex: number, colIndex: number) => void;
}

interface EditorComponentState {
  open: boolean;
  richTextEditorState: EditorState;
  cellData:
  | {
    row: number | undefined;
    col: number | undefined;
    prop: string;
    td: HTMLTableColElement | undefined;
    originalValue: any;
    cellProperties: any;
  }
  | undefined;
  selectedImageScale: ImageScale;
}

const createEmptyEditorState = () => EditorState.createEmpty();

const getClassByScale = (scale: ImageScale): string => {
  switch (scale) {
    case ImageScale.Small: return "small-image";
    case ImageScale.Large: return "large-image";
    case ImageScale.Medium:
    default: return "medium-image";
  }
};

class CustomCellEditorComponent extends BaseEditorComponent<
  EditorComponentProps,
  EditorComponentState
> {
  constructor(props: any) {
    super(props);

    this.state = {
      open: false,
      richTextEditorState: createEmptyEditorState(),
      cellData: undefined,
      selectedImageScale: ImageScale.Medium
    };
  }

  state: EditorComponentState;

  setValue(value: string) {
    const contentState = this.getContentState(value);
    const editorState = EditorState.createWithContent(contentState);
    const editorState2 = EditorState.moveFocusToEnd(editorState);
    this.updateEditorState(editorState2);
  }

  cleanupEditor() {
    const contentState = this.getContentState('');
    const editorStateWithContent = EditorState.createWithContent(contentState)
    const editorStateFocusEnd = EditorState.moveFocusToEnd(editorStateWithContent);
    this.updateEditorState(editorStateFocusEnd);
  }

  getContentState(value: string) {
    const contentState = convertFromHTML({
      htmlToEntity: (nodeName, node, createEntity) => {
        if (nodeName === 'img') {
          this.updateImageScale(node.getAttribute('data-scale') as ImageScale || ImageScale.Medium);
          return createEntity('IMAGE', 'IMMUTABLE', {
            src: node.getAttribute('src'),
            alt: node.getAttribute('alt'),
            style: node.getAttribute('style') || (node.getAttribute('data-scale') === "Small" ? "max-width: 50px;" : node.getAttribute('data-scale') === "Large" ? "max-width: 150px;" : "max-width: 100px;"),
            "data-scale": node.getAttribute('data-scale'),
          });
        } else if (nodeName === "a") {
          return createEntity(
            "LINK",
            "MUTABLE",
            {
              url: node.getAttribute("href"),
              target: node.getAttribute("target") || "_self"
            }
          )
        }
      },
      htmlToBlock: (nodeName, node) => {
        if (nodeName === 'img') {
          return {
            type: 'atomic',
            data: {},
          };
        }
      },
      htmlToStyle(nodeName, node, currentStyle) {
        if (nodeName === "ins") {
          currentStyle = currentStyle.add("UNDERLINE")
        }
        // this sets the font size per element
        if (node.style) {
          if (node.style.fontSize) {
            const sanitizedFontSize = node.style.fontSize.replace(/[^\d]/g, '');
            currentStyle = currentStyle.add(`fontsize-${sanitizedFontSize}`);
          }

          if (node.style.fontFamily) {
            const sanitizedFontFamily = node.style.fontFamily.replace(/[\'\"]/g, '');
            currentStyle = currentStyle.add(`fontfamily-${sanitizedFontFamily}`);
          }
        }
        return currentStyle;
      },
    })(value);
    return contentState;
  }

  getValue() {
    const raw = convertToRaw(this.state.richTextEditorState.getCurrentContent());
    let html = draftToHtml(raw);

    const imageEntity = Object.values(raw.entityMap).find(entity => entity.type === "IMAGE");
    if (!imageEntity) {
      return html;
    }

    const { style, ["data-scale"]: dataScale } = imageEntity.data || {};

    html = html.replace(
      /<img\b[^>]*>/i,
      (imgTag) => {
        let styleValue = "";
        if (this.state.selectedImageScale === ImageScale.Small) {
          styleValue += "max-width: 50px;";
        } else if (this.state.selectedImageScale === ImageScale.Medium) {
          styleValue += "max-width: 100px;";
        } else if (this.state.selectedImageScale === ImageScale.Large) {
          styleValue += "max-width: 150px;";
        }

        const className = getClassByScale(this.state.selectedImageScale);
        const withStyle = imgTag.includes("style=")
          ? imgTag.replace(/style="[^"]*"/i, `style="${styleValue}"`)
          : imgTag.replace("<img", `<img style="${styleValue}"`);

        const withDataScale = withStyle.includes("data-scale=")
          ? withStyle.replace(/data-scale="[^"]*"/i, `data-scale="${this.state.selectedImageScale.toString()}"`)
          : withStyle.replace(/<img\b/i, `<img data-scale="${this.state.selectedImageScale.toString()}"`);

        return withDataScale.includes("class=")
          ? withDataScale.replace(/class="[^"]*"/i, `class="${className}"`)
          : withDataScale.replace(/<img\b/i, `<img class="${className}"`);
      }
    )
    return html;
  }

  open() {
    this.setState({ open: true });
  }

  /// this is to prevent table from closing open editors randomly.
  close() {
    this.setState({ open: false });
    this.cleanupEditor();
  }

  prepare(
    row: any,
    col: any,
    prop: any,
    td: any,
    originalValue: any,
    cellProperties: any
  ) {
    super.prepare(row, col, prop, td, originalValue, cellProperties);

    this.setState({
      cellData: { row, col, prop, td, originalValue, cellProperties },
    });
  }

  updateImageScale = (scaleOption: ImageScale) => {
    this.setState({
      selectedImageScale: scaleOption
    })
  }

  updateEditorState = (richTextEditorState: EditorState) => {
    this.setState({ richTextEditorState });
  };

  handleEditorBlur = () => {
    this.props.hotTableRef.current?.hotInstance?.deselectCell();
    this.props.hotTableRef.current?.hotInstance?.selectCell(
      this.state.cellData?.row,
      this.state.cellData?.col
    );
  };

  render() {
    return (
      <div>
        <EditorComponentInner
          props={this.props}
          state={this.state}
          updateEditorState={this.updateEditorState}
          onBlur={this.handleEditorBlur}
          updateImageScale={this.updateImageScale}
          imageScale={this.state.selectedImageScale}
        />
      </div>
    );
  }
}

const EditorComponentInner: FC<{
  props: EditorComponentProps;
  state: EditorComponentState;
  updateEditorState: (richTextEditorState: EditorState) => void;
  onBlur?: () => void;
  updateImageScale?: (scaleOption: ImageScale) => void;
  imageScale?: ImageScale;
}> = ({ props, state, updateEditorState, onBlur, updateImageScale, imageScale }) => {

  const dispatch = useDispatch();
  const imageScaleOptions = [ImageScale.Small, ImageScale.Medium, ImageScale.Large];
  const getMaxWidthByScale = (scale: ImageScale) => {
    switch (scale) {
      case ImageScale.Small:
        return "50px";
      case ImageScale.Large:
        return "150px";
      case ImageScale.Medium:
      default:
        return "100px";
    }
  };

  const normalizeScale = (scale?: string): ImageScale => {
    if (scale === ImageScale.Small) {
      return ImageScale.Small;
    }
    if (scale === ImageScale.Large) {
      return ImageScale.Large;
    }
    return ImageScale.Medium;
  };

  const [selectedImageScale, setSelectedImageScale] = useState<ImageScale>(ImageScale.Medium);

  const outerDivRef = React.useRef<HTMLDivElement>(null);
  useEffect(
    function resizeEditor() {
      if (
        outerDivRef &&
        outerDivRef.current &&
        state.cellData?.td &&
        state.open
      ) {
        const rect = state.cellData.td.getBoundingClientRect();
        outerDivRef.current.style.display = "block";
        outerDivRef.current.style.left = rect.left + "px";
        outerDivRef.current.style.top = rect.top + "px";
        outerDivRef.current.style.width = "auto";
        outerDivRef.current.style.height = "auto";
      }
    },
    [state.cellData?.td, outerDivRef, outerDivRef.current, state.open]
  );

  const stopMousedownPropagation: MouseEventHandler = (e) => {
    e.stopPropagation();
  };

  const stopKeydownPropagation = (e: React.KeyboardEvent) => {
    e.stopPropagation();

    if (e.key === "Escape") {
      onBlur?.();
    }
  };

  const editorRef = useRef<Editor>(null);

  useEffect(() => {
    if (state.open) {
      outerDivRef.current?.focus();
      editorRef.current?.focusEditor();
    }
  }, [state.open]);

  useEffect(() => {
    const scaleFromCell = state.cellData?.cellProperties?.scale as string | undefined;
    setSelectedImageScale(normalizeScale(scaleFromCell || imageScale));
  }, [state.cellData?.row, state.cellData?.col, state.open]);

  const [backspaceMessageShown, setBackspaceMessageShown] = useState(false);
  const [deleteMessageShown, setDeleteMessageShown] = useState(false);

  const handlePastedText = (
    text: string,
    html: string,
    editorState: EditorState,
    onChange: (editorState: EditorState) => void
  ): boolean => {
    if (html !== undefined && html.includes("content=\"Handsontable\"")) {
      //manually set the state to the text instead of html
      const contentBlock = htmlToDraft(text);
      const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
      const editorState = EditorState.createWithContent(contentState);
      onChange(editorState);
      return true;
    }
    //return false just means let it do what it normally would have done
    return false;
  }

  const handleKeyCommand = (
    command: DraftEditorCommand,
    editorState: EditorState
  ): DraftHandleValue => {
    const content = editorState.getCurrentContent();
    const selection = editorState.getSelection();
    const currentBlockKey = selection.getStartKey();
    const blockMap = content.getBlockMap();
    const blockArray = blockMap.toArray();
    const currentIndex = blockArray.findIndex(b => b.getKey() === currentBlockKey);

    if (command === 'delete') {
      const currentBlock = blockArray[currentIndex];
      const cursorAtEnd = selection.getStartOffset() === currentBlock.getLength();
      if (cursorAtEnd && currentIndex + 1 < blockArray.length) {
        const nextBlock = blockArray[currentIndex + 1];
        if (nextBlock.getType() === 'atomic') {
          // Remove the atomic block completely
          const newBlockArray = blockArray.filter(block => block.getKey() !== nextBlock.getKey());
          const newContentState = ContentState.createFromBlockArray(newBlockArray);
          const newEditorState = EditorState.push(editorState, newContentState, 'remove-range');
          updateEditorState(EditorState.forceSelection(newEditorState, newContentState.getSelectionAfter()));
          return 'handled';
        }
      }
      if (!deleteMessageShown && content.getBlocksAsArray().some(b => b.getType() === 'atomic')) {
        dispatch(showErrorToast('You may need to hit delete multiple times to delete an image.'));
        setDeleteMessageShown(true);
      }
    }
    if (command === 'backspace') {
      const cursorAtStart = selection.getStartOffset() === 0;
      if (cursorAtStart && currentIndex > 0) {
        const prevBlock = blockArray[currentIndex - 1];
        if (prevBlock.getType() === 'atomic') {
          const newBlockArray = blockArray.filter(block => block.getKey() !== prevBlock.getKey());
          const newContentState = ContentState.createFromBlockArray(newBlockArray);
          const newEditorState = EditorState.push(editorState, newContentState, 'remove-range');
          updateEditorState(EditorState.forceSelection(newEditorState, newContentState.getSelectionAfter()));
          return 'handled';
        }
      }
      if (!backspaceMessageShown && content.getBlocksAsArray().some(b => b.getType() === 'atomic')) {
        dispatch(showErrorToast('You may need to hit backspace multiple times to delete an image.'));
        setBackspaceMessageShown(true);
      }
    }
    return 'not-handled';
  };

  const countImages = (html: string) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.querySelectorAll("img").length;
  };

  const uploadDownloadTimeImage = (data: ITimeImageData) => {
    let cellAsHtml = draftToHtml(
      convertToRaw(state.richTextEditorState.getCurrentContent())
    );

    if (countImages(cellAsHtml) > 0) {
      const alreadyImageErr = "There is already an image.";
      dispatch(showErrorToast(alreadyImageErr));
      return;
    }

    const alt = addImageNumberToLabel(data.label, data.imageNumber).replace(
      ".tiff",
      ".png"
    );

    let contentAsHTML = `${cellAsHtml}
      <img src="" alt="${alt}"/>
    <p/>
    `;

    let tableTimeImageAttachment: ITableTimeImageAttachment = {
      swGuid: props.swGuid,
      swVersion: props.swVersion,
      label: data.label,
      link: data.link,
      imageNumber: data.imageNumber,
      componentGuid: props.componentGuid!!,
      stepGuid: props.stepGuid!!,
      rowIndex: state.cellData?.row ?? -1,
      colIndex: state.cellData?.col ?? -1,
      value: contentAsHTML,
      altTagValue: alt,
      timeImageTarget: props.timeImageTarget
    };

    dispatch(downloadTableTimeImage(tableTimeImageAttachment));
    onBlur?.();
  };

  const handleImageUpload = (url: string, file: File, imagescale: ImageScale, isPastingImage?: boolean) => {
    let maxWidth: string;
    let cellAsHtml = draftToHtml(
      convertToRaw(state.richTextEditorState.getCurrentContent())
    );

    if (countImages(cellAsHtml) > 0) {
      dispatch(showErrorToast("Only one image per cell is allowed"));
      return;
    }

    maxWidth = getMaxWidthByScale(imagescale);

    // Add the image with the appropriate class

    let contentAsHTML = `<p/>${cellAsHtml}
  <img src="${url}" alt="undefined" style="max-width: ${maxWidth}" class="${getClassByScale(imagescale)}" data-scale="${imagescale}" />
<p/>`;
    const imageUpload: ITableComponentImage = {
      swGuid: props.swGuid,
      stepGuid: props.stepGuid!!,
      componentGuid: props.componentGuid!!,
      rowIndex: state.cellData?.row ?? -1,
      colIndex: state.cellData?.col ?? -1,
      imageObject: {
        file,
        localName: file.name,
      },
      swVersion: props.swVersion,
      imageUpdate: isPastingImage ? () => {
        props.handleImageUpload(file.name, contentAsHTML, state.cellData?.row ?? -1, state.cellData?.col ?? -1, imagescale)
      } : undefined
    };

    dispatch(uploadTableImage(imageUpload));
    if (!isPastingImage) {
      props.handleImageUpload(file.name, contentAsHTML, state.cellData?.row ?? -1, state.cellData?.col ?? -1, imagescale);
    }
    setSelectedImageScale(imagescale);
    onBlur?.();
  }

  const getCellHtml = () => draftToHtml(
    convertToRaw(state.richTextEditorState.getCurrentContent())
  );

  const updateImageScaleInHtml = (html: string, scale: ImageScale) => {
    const wrapper = document.createElement("div");
    wrapper.innerHTML = html;
    const maxWidth = getMaxWidthByScale(scale);

    wrapper.querySelectorAll("img").forEach((img) => {
      img.setAttribute("data-scale", scale);
      img.style.maxWidth = maxWidth;
      img.style.height = "auto";
      img.style.display = "block";
      img.style.margin = "5px 0";
      img.className = getClassByScale(scale);
    });
    if (updateImageScale) {
      updateImageScale(scale);
    }

    return wrapper.innerHTML;
  };

  const handleScaleUpdate = (scaleOption: ImageScale) => {
    const cellAsHtml = getCellHtml();

    if (countImages(cellAsHtml) === 0) {
      return;
    }

    const updatedHtml = updateImageScaleInHtml(cellAsHtml, scaleOption);
    setSelectedImageScale(scaleOption);
    props.handleImageScaleUpdate(updatedHtml, state.cellData?.row ?? -1, state.cellData?.col ?? -1, scaleOption);
  };

  const handleDeleteImg = () => {
    let cellAsHtml = getCellHtml();
    const div = document.createElement("div");
    div.innerHTML = cellAsHtml;
    const filteredNodes = Array.from(div.childNodes).filter(node => {
      if (node.nodeName === "IMG") return false;
      if (node.nodeName === "P") {
        const text = (node.textContent || "").replace(/\u00A0/g, '').trim();
        return text.length > 0;
      }
      return true;
    });

    const resultHTML = filteredNodes
      .map(node => (node instanceof Element ? node.outerHTML : ''))
      .join('\n');
    props.updateCellAfterDeleteImg(resultHTML, state.cellData?.row ?? -1, state.cellData?.col ?? -1);
    onBlur?.();
  }


  const getCustomEditorButtons = () => {
    let components = [];

    if (props.enableTimeUpload) {
      components.push(
        <TableTimeImageComponent
          allowEdit={!props.disabled}
          uploadDownloadTimeImage={uploadDownloadTimeImage}
          timeImageData={props.timeImageData!!}
          swType={props.swType}
        />);
    }

    components.push(
      <TableImageComponent handleImageUpload={handleImageUpload} />
    );

    let cellAsHtml = draftToHtml(
      convertToRaw(state.richTextEditorState.getCurrentContent())
    );

    if (countImages(cellAsHtml) > 0) {
      const resolvedScale = normalizeScale(state.cellData?.cellProperties?.scale || imageScale);
      components.push(
        <div className="rdw-option-wrapper" style={{ width: "auto", padding: "0 8px" }}>
          <CustomDropdown
            key={`img-scale-${state.cellData?.row}-${state.cellData?.col}-${resolvedScale}`}
            options={imageScaleOptions}
            onSelect={handleScaleUpdate}
            currentValue={resolvedScale}
          />
        </div>
      );
      components.push(
        <DeleteImageComponent handleDeleteImg={handleDeleteImg} />
      );
    }

    return components;
  }

  const handleOnPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const items = e.clipboardData?.items;
    if (items) {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === "file" && item.type.startsWith("image/") && e.clipboardData.getData("text/plain") === "") {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) {
            const reader = new FileReader();
            let hasAlreadyAnImage = false;
            reader.onload = () => {
              const src = reader.result as string;
              const contentState = state.richTextEditorState.getCurrentContent();
              hasAlreadyAnImage = contentState.getBlocksAsArray().some(block => {
                if (block.getType() !== "atomic") return false;

                const entityKey = block.getEntityAt(0);
                if (!entityKey) return false;

                const entity = contentState.getEntity(entityKey)
                return entity.getType() === "IMAGE";
              })


              if (!hasAlreadyAnImage) {
                const contentStateWithEntity = contentState.createEntity(
                  "IMAGE",
                  "IMMUTABLE",
                  { src }
                );
                const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
                const newEditorState = AtomicBlockUtils.insertAtomicBlock(
                  state.richTextEditorState,
                  entityKey,
                  " "
                )
                updateEditorState(newEditorState)
              }

            }
            if (!hasAlreadyAnImage) {
              reader.readAsDataURL(file)
              handleImageUpload(URL.createObjectURL(file), file, ImageScale.Medium, true)
            }

          }
        }
      }
    }
  }

  return (
    state.open && (
      <div
        style={{
          display: "none",
          position: "absolute",
          left: 0,
          top: 0,
          background: "#fff",
          border: "1px solid #000",
          padding: "15px",
          zIndex: 999,
        }}
        ref={outerDivRef}
        onMouseDown={stopMousedownPropagation}
        onKeyDown={stopKeydownPropagation}
        onPasteCapture={handleOnPaste}
      >
        <Editor
          toolbarClassName="rte-toolbar-class-table"
          editorClassName="rte-editor-class-table"
          editorState={state.richTextEditorState}
          onEditorStateChange={updateEditorState}
          ref={editorRef}
          handleKeyCommand={handleKeyCommand}
          handlePastedText={handlePastedText}
          toolbar={{
            options: [
              "inline",
              "list",
              "link",
              "textAlign",
              "fontSize",
              "fontFamily",
            ],
            inline: {
              inDropdown: true,
              options: ["bold", "italic", "underline"],
            },
            list: {
              inDropdown: true,
            },
            image: {
              alignmentEnabled: false,
            },
            fontSize: {
              options: [
                6, 8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60, 72, 84
              ],
            },
            fontFamily: {
              options: [
                "Arial",
                "Georgia",
                "Impact",
                "Tahoma",
                "Times New Roman",
                "Verdana",
              ],
            },
            textAlign: {
              inDropdown: true,
              options: ["left", "center", "right", "justify"],
            },
          }}

          toolbarCustomButtons={getCustomEditorButtons()}
          customBlockRenderFunc={(block) => {
            if (block.getType() === "atomic") {
              return {
                component: CustomImageComponent,
                editable: false,
                props: {
                  scale: selectedImageScale
                }
              };
            }
            return undefined;
          }}
        />
      </div>
    )
  );
};

const CustomImageComponent: React.FC<any> = (props) => {

  const { scale } = props.blockProps;
  const entity = props.contentState.getEntity(props.block.getEntityAt(0));
  const { src, alt, } = entity.getData();
  const effectiveScale = scale || ImageScale.Medium;

  // Decide max-width based on scale
  let maxWidth = '100px';
  switch (effectiveScale) {
    case ImageScale.Small:
      maxWidth = '50px';
      break;
    case ImageScale.Medium:
      maxWidth = '100px';
      break;
    case ImageScale.Large:
      maxWidth = '150px';
      break;
  }

  return (
    <img
      src={src}
      alt={alt || 'Image'}
      className={getClassByScale(effectiveScale)}
      style={{
        maxWidth,
        height: 'auto',
        display: 'block',
        margin: '5px 0',
      }}
    />
  );
};

export default CustomCellEditorComponent;

